/**
 * INYRON Ω-Genome
 *
 * Evidence-based causal atlas layered on top of Ω-Spectrum. It learns stable
 * mode -> outcome associations, but never mutates gameplay directly. Sovereign
 * game adapters decide whether a proposal is safe and meaningful to apply.
 */
import type { EmergentSpectralMode } from "./omega-spectrum";

export type OmegaEffectChannel =
  | "physics" | "material" | "life" | "mind" | "combat"
  | "weather" | "audio" | "visual" | "navigation" | "economy" | "system";

export interface OmegaOutcomeSample {
  modeId: string;
  channel: OmegaEffectChannel;
  effect: string;
  value: number;
  atSeconds: number;
  modeStability?: number;
  contextTags?: string[];
}

export interface OmegaAssociation {
  id: string;
  modeId: string;
  channel: OmegaEffectChannel;
  effect: string;
  evidence: number;
  meanEffect: number;
  variance: number;
  confidence: number;
  modeStability: number;
  firstSeenAt: number;
  lastSeenAt: number;
  contextTags: string[];
}

export interface OmegaEffectProposal {
  associationId: string;
  modeId: string;
  channel: OmegaEffectChannel;
  effect: string;
  intensity: number;
  confidence: number;
  evidence: number;
}

export interface OmegaGenomeSnapshot {
  version: 1;
  associations: OmegaAssociation[];
}

export interface OmegaGenomeOptions {
  maxAssociations: number;
  confidenceEvidenceScale: number;
  maxContextTags: number;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const clamp01 = (v: number) => clamp(v, 0, 1);
const finite = (v: unknown, fallback = 0) =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

function hash32(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function cloneAssociation(value: OmegaAssociation): OmegaAssociation {
  return { ...value, contextTags: [...value.contextTags] };
}

export class OmegaGenomeRegistry {
  private readonly associations = new Map<string, OmegaAssociation>();
  readonly options: OmegaGenomeOptions;

  constructor(options: Partial<OmegaGenomeOptions> = {}) {
    this.options = {
      maxAssociations: Math.max(64, Math.trunc(options.maxAssociations ?? 4096)),
      confidenceEvidenceScale: Math.max(1, finite(options.confidenceEvidenceScale, 6)),
      maxContextTags: Math.max(1, Math.trunc(options.maxContextTags ?? 24)),
    };
  }

  observe(sample: OmegaOutcomeSample): OmegaAssociation | undefined {
    if (!sample.modeId || !sample.effect || !Number.isFinite(sample.value) || !Number.isFinite(sample.atSeconds)) {
      return undefined;
    }
    const key = sample.modeId + "|" + sample.channel + "|" + sample.effect;
    const id = "ΩG-" + hash32(key).toString(36).toUpperCase().padStart(7, "0");
    const value = clamp(sample.value, -1, 1);
    const stability = clamp01(sample.modeStability ?? 0.5);
    const current = this.associations.get(id);

    if (!current) {
      const association: OmegaAssociation = {
        id,
        modeId: sample.modeId,
        channel: sample.channel,
        effect: sample.effect,
        evidence: 1,
        meanEffect: value,
        variance: 0,
        confidence: this.confidence(1, 0, stability),
        modeStability: stability,
        firstSeenAt: sample.atSeconds,
        lastSeenAt: sample.atSeconds,
        contextTags: this.mergeTags([], sample.contextTags),
      };
      this.associations.set(id, association);
      this.trim();
      return cloneAssociation(association);
    }

    const oldEvidence = current.evidence;
    const nextEvidence = oldEvidence + 1;
    const delta = value - current.meanEffect;
    const nextMean = current.meanEffect + delta / nextEvidence;
    const nextVariance = (
      oldEvidence * current.variance + delta * (value - nextMean)
    ) / nextEvidence;

    current.evidence = nextEvidence;
    current.meanEffect = clamp(nextMean, -1, 1);
    current.variance = clamp01(nextVariance);
    current.modeStability = clamp01(
      current.modeStability * 0.85 + stability * 0.15,
    );
    current.confidence = this.confidence(
      current.evidence,
      current.variance,
      current.modeStability,
    );
    current.lastSeenAt = sample.atSeconds;
    current.contextTags = this.mergeTags(current.contextTags, sample.contextTags);
    return cloneAssociation(current);
  }

  association(id: string): OmegaAssociation | undefined {
    const value = this.associations.get(id);
    return value ? cloneAssociation(value) : undefined;
  }

  associationsForMode(modeId: string): OmegaAssociation[] {
    return [...this.associations.values()]
      .filter(item => item.modeId === modeId)
      .sort((a, b) => b.confidence - a.confidence || b.evidence - a.evidence || a.id.localeCompare(b.id))
      .map(cloneAssociation);
  }

  proposalsForMode(
    mode: Pick<EmergentSpectralMode, "id" | "amplitude" | "stability">,
    minConfidence = 0.55,
    limit = 8,
  ): OmegaEffectProposal[] {
    return this.associationsForMode(mode.id)
      .map(item => ({
        associationId: item.id,
        modeId: item.modeId,
        channel: item.channel,
        effect: item.effect,
        intensity: clamp(item.meanEffect * mode.amplitude, -1, 1),
        confidence: clamp01(item.confidence * mode.stability),
        evidence: item.evidence,
      }))
      .filter(item => item.confidence >= clamp01(minConfidence))
      .slice(0, Math.max(0, Math.trunc(limit)));
  }

  strongest(limit = 32): OmegaAssociation[] {
    return [...this.associations.values()]
      .sort((a, b) => b.confidence - a.confidence || b.evidence - a.evidence || a.id.localeCompare(b.id))
      .slice(0, Math.max(0, Math.trunc(limit)))
      .map(cloneAssociation);
  }

  snapshot(): OmegaGenomeSnapshot {
    return {
      version: 1,
      associations: [...this.associations.values()]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(cloneAssociation),
    };
  }

  restore(snapshot: unknown): number {
    if (!snapshot || typeof snapshot !== "object") return 0;
    const candidate = snapshot as Partial<OmegaGenomeSnapshot>;
    if (candidate.version !== 1 || !Array.isArray(candidate.associations)) return 0;
    this.associations.clear();
    for (const raw of candidate.associations) {
      if (!raw?.id || !raw.modeId || !raw.effect) continue;
      const item: OmegaAssociation = {
        ...raw,
        evidence: Math.max(1, Math.trunc(finite(raw.evidence, 1))),
        meanEffect: clamp(finite(raw.meanEffect), -1, 1),
        variance: clamp01(finite(raw.variance)),
        confidence: clamp01(finite(raw.confidence)),
        modeStability: clamp01(finite(raw.modeStability, 0.5)),
        firstSeenAt: Math.max(0, finite(raw.firstSeenAt)),
        lastSeenAt: Math.max(0, finite(raw.lastSeenAt)),
        contextTags: this.mergeTags([], raw.contextTags),
      };
      this.associations.set(item.id, item);
    }
    this.trim();
    return this.associations.size;
  }

  clear(): void { this.associations.clear(); }

  private confidence(evidence: number, variance: number, stability: number): number {
    const support = 1 - Math.exp(-evidence / this.options.confidenceEvidenceScale);
    const consistency = 1 - clamp01(variance);
    return clamp01(support * consistency * (0.35 + 0.65 * stability));
  }

  private mergeTags(existing: string[], incoming?: string[]): string[] {
    return [...new Set([
      ...existing,
      ...(incoming ?? []).filter(tag => typeof tag === "string" && tag.length > 0),
    ])].sort().slice(-this.options.maxContextTags);
  }

  private trim(): void {
    if (this.associations.size <= this.options.maxAssociations) return;
    const weakest = [...this.associations.values()]
      .sort((a, b) => a.confidence - b.confidence || a.evidence - b.evidence || a.lastSeenAt - b.lastSeenAt);
    const removeCount = this.associations.size - this.options.maxAssociations;
    for (let i = 0; i < removeCount; i += 1) this.associations.delete(weakest[i].id);
  }
}
